'use client';
import React from 'react';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faFileAlt } from '@fortawesome/pro-solid-svg-icons/faFileAlt';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import Image from 'next/image';

import styles from './ItemThumbnail.module.css';
import { Duration } from './Duration';
import ProgressBar from '../ProgressBar';
import { useIntl } from 'react-intl';

export type ItemThumbnailProps = {
  thumbnailURL: string;
  altText?: string;
  playIcon: boolean;
  showDocIcon?: boolean;
  minHeight: string; // it's required prop since initialy component doesn't know about image size and by default it's 0
  permanentDuration?: boolean;
  duration: string | null;
  watchPercent?: number;
  imageSx?: React.CSSProperties;
  dataTestId?: string;
  title: string;
  unoptimized?: boolean;
  toBeRetired?: boolean;
  iconLabel?: string;
  archivedStatus?: boolean;
};

/**
 * Returns ItemThumbnail component.
 */
const ItemThumbnail = (props: ItemThumbnailProps) => {
  const {
    thumbnailURL,
    altText = '',
    playIcon = true,
    showDocIcon,
    permanentDuration,
    minHeight,
    duration,
    watchPercent,
    imageSx,
    dataTestId,
    title,
    unoptimized = false,
    toBeRetired,
    iconLabel,
    archivedStatus,
  } = props;

  const colors = useColors();
  const { formatMessage } = useIntl();

  const [hideDuration, setHideDuration] = React.useState(false);

  return (
    <Flex
      data-testid={dataTestId ? `${dataTestId}-thumbnail` : 'thumbnail'}
      className={`relative justify-center width-auto h-full min-h-[${minHeight}] bg-white border-2 border-blue-600 rounded-md overflow-hidden ${
        archivedStatus ? 'cursor-default grayscale' : 'hover:cursor-pointer'
      }`}
    >
      <Image
        className={`${styles.image} items-center justify-center object-contain`}
        width={200} // Magic numbers; 16 by 9 aspect ratio
        height={112.5}
        unoptimized={unoptimized}
        style={{
          ...imageSx,
        }}
        src={thumbnailURL}
        alt={altText}
        aria-label={formatMessage(
          {
            id: 'item.thumbnail.aria-label-title',
            defaultMessage: '{title}',
          },
          { title },
        )}
      />

      {/* The playIcon and duration can annoyingly overlap when there's a really small thumbnail.
          by transparentifying the duration when the video is hovered on, and setting it to 0px
          when it's hovered upon so the play icon still appears, it allows for the duration to
          never be in the way. */}
      {(playIcon || showDocIcon) && !toBeRetired && (
        <Box>
          <Flex
            data-testid="thumbnail-layout-with-opacity"
            className="absolute top-0 left-0 w-full h-full justify-center items-center transition ease-in-out delay-100 bg-black/20 rounded-md opacity-0 hover:opacity-100 focus:opacity-100"
            onMouseEnter={() => setHideDuration(true)}
            onMouseLeave={() => setHideDuration(false)}
            onFocus={() => setHideDuration(true)}
            onBlur={() => setHideDuration(false)}
            tabIndex={0}
          >
            <Box className="px-3 py-2 bg-black rounded-lg opacity-100">
              <Icon
                icon={playIcon ? faPlay : faFileAlt}
                color={colors.white}
                aria-label={iconLabel}
                aria-hidden="false"
              />
            </Box>
          </Flex>
        </Box>
      )}

      {duration ? (
        <Box
          className={`absolute bottom-[10%] right-[5%] transition-opacity ease-in-out delay-100 ${
            hideDuration && !permanentDuration
              ? 'opacity-0 invisible'
              : 'opacity-100 visible'
          }`}
        >
          <Duration duration={duration} />
        </Box>
      ) : null}

      {watchPercent ? (
        <Box className="absolute bottom-0 w-full">
          <ProgressBar height={4} barColor="#FF0000" progress={watchPercent} />
        </Box>
      ) : null}
    </Flex>
  );
};

export default ItemThumbnail;
